import React, { useCallback, useState, useRef, Fragment, lazy } from 'react';
import { Hero } from '../components/Hero';
import { Section } from '../components/Section';
import { PullQuote } from '../components/PullQuote';
import { PurchaseSection } from '../components/PurchaseSection';
import { OrderBump } from '../components/OrderBump';
import { CTA } from '../components/CTA';
import { SpiritualLoader } from '../components/SpiritualLoader';
import { StepCard } from '../components/StepCard';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
export function SalesPage() {
  const {
    t
  } = useLanguage();
  const purchaseRef = useRef<HTMLDivElement>(null);
  const [orderBumpChecked, setOrderBumpChecked] = useState(false);
  const scrollToPurchase = useCallback(() => {
    purchaseRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);
  return <>
      <SpiritualLoader data-id="element-198" />
      <PurchaseNotification data-id="element-199" />

      <div className="min-h-screen relative" data-id="element-200">
        <Hero onScrollToPurchase={scrollToPurchase} data-id="element-201" />

        <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-10 space-y-10 md:space-y-16" data-id="element-202">
          <Section data-id="element-203">
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-204">
              {t.intro.p2}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-4 md:mt-6" data-id="element-205">
              {t.intro.p3}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-4 md:mt-6 italic" data-id="element-206">
              {t.intro.p4}
            </p>
          </Section>

          <Section variant="gold" data-id="element-207">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-208">
              {t.pain.title}
            </h2>
            {t.pain.p1.split('\n\n').map((paragraph, i) => <Fragment key={i} data-id="element-209">
                <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-4 first:mt-0" data-id="element-210">
                  {paragraph}
                </p>
                {i === 1 && <div className="mt-6 md:mt-8" data-id="element-211">
                    <img src="https://cdn.magicpatterns.com/uploads/wuH2APBaXgseTk1nENdU9i/ChatGPT_Image_20_feb_2026,_08_30_15.webp" alt="Mujer con ansiedad frente al dinero" className="w-full rounded-2xl shadow-2xl" loading="lazy" decoding="async" data-id="element-212" />
                  </div>}
              </Fragment>)}
            {t.pain.p2.split('\n\n').map((paragraph, i) => <p key={`p2-${i}`} className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-4" data-id="element-213">
                {paragraph}
              </p>)}
          </Section>

          <Section data-id="element-214">
            <h3 className="font-serif text-xl md:text-3xl text-[#2C4A3E] mb-4 md:mb-6" data-id="element-215">
              {t.blocks.sabotage.title}
            </h3>
            {t.blocks.sabotage.text.split('\n\n').map((paragraph, i) => <p key={i} className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-4 first:mt-0" data-id="element-216">
                {paragraph}
              </p>)}
          </Section>

          {/* Decorative divider */}
          <div className="flex flex-col items-center -mb-6 md:-mb-10" data-id="element-217">
            <svg width="200" height="20" viewBox="0 0 200 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-44 md:w-56" data-id="element-218">
              <line x1="0" y1="8" x2="88" y2="8" stroke="#2C4A3E" strokeWidth="1" data-id="element-219" />
              <line x1="112" y1="8" x2="200" y2="8" stroke="#2C4A3E" strokeWidth="1" data-id="element-220" />
              <path d="M88 8 L100 18 L112 8" stroke="#2C4A3E" strokeWidth="1" fill="none" strokeLinejoin="round" data-id="element-221" />
            </svg>
          </div>

          <div className="mt-0 mb-6 md:mb-8" data-id="element-222">
            <img src="https://cdn.magicpatterns.com/uploads/jURujNLoHUhiSWG4ekgoJB/ChatGPT_Image_20_feb_2026,_08_38_41.webp" alt="No es sólo un bloqueo emocional. Está afectando tus ingresos cada mes." className="w-full rounded-2xl shadow-2xl" loading="lazy" decoding="async" data-id="element-223" />
          </div>

          {/* Tarjetas de patrones financieros */}
          <div data-id="element-224">
            <div className="grid grid-cols-3 gap-2 md:gap-5 mb-6 md:mb-10" data-id="element-225">
              {/* Card 1 */}
              <div className="bg-gradient-to-b from-[#3A4A38] via-[#2D3B2B] to-[#232E22] rounded-lg md:rounded-xl p-3 md:p-6 shadow-lg border border-[#D4A574]/10 text-center" data-id="element-226">
                <div className="w-9 h-9 md:w-12 md:h-12 mx-auto mb-2 md:mb-4 rounded-full bg-gradient-to-br from-[#E8B86D] to-[#C87941] flex items-center justify-center" data-id="element-227">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="#2C4A3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-228">
                    <line x1="12" y1="1" x2="12" y2="23" data-id="element-229" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" data-id="element-230" />
                  </svg>
                </div>
                <h4 className="font-serif text-[11px] md:text-xl text-[#E8D5B5] font-bold mb-2 md:mb-3 leading-tight" data-id="element-231">
                  {t.blocks.cards.card1Title}
                </h4>
                <div className="w-8 md:w-12 h-px bg-[#D4A574]/40 mx-auto mb-2 md:mb-3" data-id="element-232"></div>
                <p className="text-[10px] md:text-base text-[#D4C4A8] leading-snug md:leading-relaxed mb-2 md:mb-3" data-id="element-233">
                  {t.blocks.cards.card1Text}
                </p>
                <div className="w-8 md:w-12 h-px bg-[#D4A574]/40 mx-auto mb-2 md:mb-3" data-id="element-234"></div>
                <p className="text-[10px] md:text-base text-[#E8D5B5] font-semibold leading-snug" data-id="element-235">
                  {t.blocks.cards.card1Bottom}
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-gradient-to-b from-[#3A4A38] via-[#2D3B2B] to-[#232E22] rounded-lg md:rounded-xl p-3 md:p-6 shadow-lg border border-[#D4A574]/10 text-center" data-id="element-236">
                <div className="w-9 h-9 md:w-12 md:h-12 mx-auto mb-2 md:mb-4 rounded-full bg-gradient-to-br from-[#E8B86D] to-[#C87941] flex items-center justify-center" data-id="element-237">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="#2C4A3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-238">
                    <polyline points="23 4 23 10 17 10" data-id="element-239" />
                    <polyline points="1 20 1 14 7 14" data-id="element-240" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" data-id="element-241" />
                  </svg>
                </div>
                <h4 className="font-serif text-[11px] md:text-xl text-[#E8D5B5] font-bold mb-2 md:mb-3 leading-tight" data-id="element-242">
                  {t.blocks.cards.card2Title}
                </h4>
                <div className="w-8 md:w-12 h-px bg-[#D4A574]/40 mx-auto mb-2 md:mb-3" data-id="element-243"></div>
                <p className="text-[10px] md:text-base text-[#D4C4A8] leading-snug md:leading-relaxed mb-2 md:mb-3" data-id="element-244">
                  {t.blocks.cards.card2Text}
                </p>
                <div className="w-8 md:w-12 h-px bg-[#D4A574]/40 mx-auto mb-2 md:mb-3" data-id="element-245"></div>
                <p className="text-[10px] md:text-base text-[#E8D5B5] font-semibold leading-snug" data-id="element-246">
                  {t.blocks.cards.card2Bottom}
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-gradient-to-b from-[#3A4A38] via-[#2D3B2B] to-[#232E22] rounded-lg md:rounded-xl p-3 md:p-6 shadow-lg border border-[#D4A574]/10 text-center" data-id="element-247">
                <div className="w-9 h-9 md:w-12 md:h-12 mx-auto mb-2 md:mb-4 rounded-full bg-gradient-to-br from-[#E8B86D] to-[#C87941] flex items-center justify-center" data-id="element-248">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="#2C4A3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-249">
                    <circle cx="12" cy="12" r="10" data-id="element-250" />
                    <circle cx="12" cy="12" r="6" data-id="element-251" />
                    <circle cx="12" cy="12" r="2" data-id="element-252" />
                  </svg>
                </div>
                <h4 className="font-serif text-[11px] md:text-xl text-[#E8D5B5] font-bold mb-2 md:mb-3 leading-tight" data-id="element-253">
                  {t.blocks.cards.card3Title}
                </h4>
                <div className="w-8 md:w-12 h-px bg-[#D4A574]/40 mx-auto mb-2 md:mb-3" data-id="element-254"></div>
                <p className="text-[10px] md:text-base text-[#D4C4A8] leading-snug md:leading-relaxed mb-2 md:mb-3" data-id="element-255">
                  {t.blocks.cards.card3Text}
                </p>
                <div className="w-8 md:w-12 h-px bg-[#D4A574]/40 mx-auto mb-2 md:mb-3" data-id="element-256"></div>
                <p className="text-[10px] md:text-base text-[#E8D5B5] font-semibold leading-snug" data-id="element-257">
                  {t.blocks.cards.card3Bottom}
                </p>
              </div>
            </div>

            {/* Costo del patrón */}
            <div className="text-center space-y-1 md:space-y-3" data-id="element-258">
              <p className="font-serif text-base md:text-2xl text-[#2C4A3E] italic" data-id="element-259">
                {t.blocks.cost.intro}
              </p>
              <div className="space-y-0 md:space-y-1" data-id="element-260">
                <p className="font-serif text-sm md:text-xl text-[#8B4513] font-bold" data-id="element-261">
                  {t.blocks.cost.year}
                </p>
                <p className="font-serif text-base md:text-2xl text-[#8B4513] font-bold" data-id="element-262">
                  {t.blocks.cost.fiveYears}
                </p>
                <p className="font-serif text-lg md:text-3xl text-[#8B4513] font-bold" data-id="element-263">
                  {t.blocks.cost.tenYears}
                </p>
              </div>
              <p className="font-serif text-sm md:text-xl text-[#2C4A3E] font-semibold italic mt-2 md:mt-3" data-id="element-264">
                {t.blocks.cost.closing}
              </p>
            </div>

            {/* Decorative divider */}
            <div className="flex flex-col items-center mt-6 md:mt-8" data-id="element-265">
              <svg width="200" height="20" viewBox="0 0 200 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-44 md:w-56" data-id="element-266">
                <line x1="0" y1="8" x2="88" y2="8" stroke="#2C4A3E" strokeWidth="1" data-id="element-267" />
                <line x1="112" y1="8" x2="200" y2="8" stroke="#2C4A3E" strokeWidth="1" data-id="element-268" />
                <path d="M88 8 L100 18 L112 8" stroke="#2C4A3E" strokeWidth="1" fill="none" strokeLinejoin="round" data-id="element-269" />
              </svg>
            </div>
          </div>

          <Section variant="gold" data-id="element-270">
            <h3 className="font-serif text-xl md:text-3xl text-[#2C4A3E] mb-4 md:mb-6" data-id="element-271">
              {t.blocks.guilt.title}
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-6 md:mb-8" data-id="element-272">
              {t.blocks.guilt.text}
            </p>
            <div className="my-6 md:my-8" data-id="element-273">
              <img src="https://cdn.magicpatterns.com/uploads/oENMUXZi65gxsMkCp1X9Ur/ChatGPT_Image_30_nov_2025,_18_11_48.webp" alt="Tres generaciones de mujeres trabajando" className="w-full rounded-2xl shadow-2xl" loading="lazy" decoding="async" data-id="element-274" />
            </div>
          </Section>

          <Section data-id="element-275">
            <h3 className="font-serif text-xl md:text-3xl text-[#2C4A3E] mb-4 md:mb-6" data-id="element-276">
              {t.blocks.fear.title}
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-277">
              {'Quieres abundancia\u2026 pero temes volverte superficial, fr\xEDa o "materialista". Temes que la gente cambie contigo. Temes perder tu esencia.\n\nTu cuerpo reacciona antes que t\xFA: presi\xF3n en el pecho, vac\xEDo en el est\xF3mago, evasi\xF3n, incomodidad para cobrar, miedo a pedir, culpa al recibir.'}
            </p>
          </Section>

          <PullQuote data-id="element-278">{t.pullQuote}</PullQuote>

          <Section variant="gold" data-id="element-279">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-280">
              {t.origin.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-281">
              {t.origin.p1}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-282">
              {t.origin.p2}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6 italic" data-id="element-283">
              {t.origin.p3}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-284">
              {'Hasta que no sanes esa ra\xEDz, el dinero seguir\xE1 entrando y saliendo sin quedarse.'}
            </p>
            <p className="text-base md:text-lg font-bold italic leading-relaxed mt-5 md:mt-7 text-center text-[#8B4513]" data-id="element-285">
              No es tu culpa pero sí tu momento de sanar ↓
            </p>
          </Section>

          <Section data-id="element-286">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-287">
              {t.solution.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-288">
              {t.solution.intro1}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-6 md:mb-8" data-id="element-289">
              {t.solution.intro2}
            </p>
            <div className="space-y-4 md:space-y-6" data-id="element-290">
              {t.solution.steps.map((step, index) => <StepCard key={index} number={index + 1} text={step} delay={0} data-id="element-291" />)}
            </div>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-6 md:mt-8 font-semibold" data-id="element-292">
              {t.solution.closing}
            </p>
          </Section>

          <Section variant="green" data-id="element-293">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-294">
              {t.vision.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-295">
              {'Imagina hablar de dinero sin tensi\xF3n.\nCobrar sin culpa. Vibrar estabilidad.'}
            </p>

            <div className="my-6 md:my-10" data-id="element-296">
              <img src="https://cdn.magicpatterns.com/uploads/cuUBuoMNkc9yuu68uofHKx/ChatGPT_Image_3_dic_2025,_12_26_55.webp" alt="Mujer en paz sosteniendo luz interior" className="w-full rounded-2xl shadow-2xl" loading="lazy" decoding="async" data-id="element-297" />
            </div>
            <ul className="space-y-3 md:space-y-4 text-base md:text-lg text-[#4A4A4A]" data-id="element-298">
              {t.vision.items.map((item, index) => <li key={index} className="flex items-start" data-id="element-299">
                  <span className="text-[#D4A574] mr-3" data-id="element-300">•</span>
                  <span data-id="element-301">{item}</span>
                </li>)}
            </ul>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-6 md:mt-8 italic" data-id="element-302">
              {t.vision.closing}
            </p>
          </Section>

          {/* Mid-page CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} className="text-center py-2" data-id="element-303">
            <p className="text-sm md:text-base text-[#4A4A4A] mb-4 font-medium tracking-wide" data-id="element-304">
              {t.midCta.ready}
            </p>
            <motion.a href="#oferta" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} className="inline-block px-8 md:px-12 py-4 md:py-5 rounded-full text-base md:text-lg font-semibold text-white transition-all duration-300" style={{
            background: '#E4574F',
            border: '1.5px solid rgba(228, 87, 79, 0.6)',
            boxShadow: '0 0 0 1px rgba(255,140,130,0.25), 0 4px 20px rgba(228,87,79,0.25)'
          }} data-id="element-305">
              {t.midCta.button}
            </motion.a>
            <p className="text-xs md:text-sm text-[#8B8B8B] mt-3" data-id="element-306">
              {t.midCta.subtext}
            </p>
          </motion.div>

          <Section data-id="element-307">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-308">
              {'La verdad que libera'}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-309">
              {'Todo lo que has intentado hasta ahora trabaja una sola capa:'}
            </p>
            <ul className="space-y-2 md:space-y-3 text-base md:text-lg text-[#4A4A4A] mb-4 md:mb-6" data-id="element-310">
              {t.truth.methods.map((method, index) => <li key={index} className="flex items-start" data-id="element-311">
                  <span className="text-[#D4A574] mr-3" data-id="element-312">•</span>
                  <span data-id="element-313">{method}</span>
                </li>)}
            </ul>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A]" data-id="element-314">
              {t.truth.p1}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mt-4 md:mt-6 font-semibold" data-id="element-315">
              {t.truth.p2}
            </p>
          </Section>

          {/* Decorative divider before testimonials */}
          <div className="flex flex-col items-center -mb-6 md:-mb-10" data-id="element-316">
            <svg width="200" height="20" viewBox="0 0 200 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-44 md:w-56" data-id="element-317">
              <line x1="0" y1="8" x2="88" y2="8" stroke="#2C4A3E" strokeWidth="1" data-id="element-318" />
              <line x1="112" y1="8" x2="200" y2="8" stroke="#2C4A3E" strokeWidth="1" data-id="element-319" />
              <path d="M88 8 L100 18 L112 8" stroke="#2C4A3E" strokeWidth="1" fill="none" strokeLinejoin="round" data-id="element-320" />
            </svg>
          </div>

          {/* Testimonials */}
          <div data-id="element-321">
            <div className="text-center mb-6 md:mb-8" data-id="element-322">
              <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-3 md:mb-4" data-id="element-323">
                {t.testimonials.title}
              </h2>
              <p className="text-base md:text-lg text-[#8B4513] italic" data-id="element-324">
                {t.testimonials.subtitle}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 md:gap-5" data-id="element-325">
              {t.testimonials.cards.map((card, index) => <div key={index} className="rounded-xl md:rounded-2xl bg-white/60 border border-[#D4A574]/20 shadow-md overflow-hidden text-center" data-id="element-326">
                  <div className="w-full aspect-[3/4] overflow-hidden" data-id="element-327">
                    <img src={card.image} alt={`${card.name} - ${card.country}`} className="w-full h-full object-cover object-top" loading="lazy" decoding="async" data-id="element-328" />
                  </div>
                  <div className="p-3 md:p-5" data-id="element-329">
                    <div className="w-8 md:w-12 h-px bg-[#D4A574]/40 mx-auto mb-2 md:mb-4" data-id="element-330"></div>
                    <p className="text-[10px] md:text-xs uppercase tracking-wider text-[#8B4513] font-bold mb-1 md:mb-2" data-id="element-331">
                      {t.testimonials.before}
                    </p>
                    <p className="text-[10px] md:text-sm text-[#4A4A4A] italic leading-snug md:leading-relaxed mb-2 md:mb-4" data-id="element-332">
                      "{card.before}"
                    </p>
                    <div className="w-8 md:w-12 h-px bg-[#D4A574]/40 mx-auto my-2 md:my-3" data-id="element-333"></div>
                    <p className="text-[10px] md:text-xs uppercase tracking-wider text-[#2C4A3E] font-bold mb-1 md:mb-2" data-id="element-334">
                      {t.testimonials.after}
                    </p>
                    <p className="text-[10px] md:text-sm text-[#2C4A3E] font-semibold leading-snug md:leading-relaxed" data-id="element-335">
                      "{card.after}"
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Decorative divider after testimonials */}
          <div className="flex flex-col items-center -mb-6 md:-mb-10" data-id="element-336">
            <svg width="200" height="20" viewBox="0 0 200 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-44 md:w-56" data-id="element-337">
              <line x1="0" y1="8" x2="88" y2="8" stroke="#2C4A3E" strokeWidth="1" data-id="element-338" />
              <line x1="112" y1="8" x2="200" y2="8" stroke="#2C4A3E" strokeWidth="1" data-id="element-339" />
              <path d="M88 8 L100 18 L112 8" stroke="#2C4A3E" strokeWidth="1" fill="none" strokeLinejoin="round" data-id="element-340" />
            </svg>
          </div>

          <Section data-id="element-341">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-342">
              {t.benefits.title}
            </h2>
            <ul className="space-y-3 md:space-y-4 text-base md:text-lg text-[#4A4A4A]" data-id="element-343">
              {t.benefits.items.map((item, index) => {
              const hasCheck = item.startsWith('✔');
              const checkmark = hasCheck ? item.slice(0, 1) : null;
              const rest = hasCheck ? item.slice(1).trimStart() : item;
              return <li key={index} className="flex items-start" data-id="element-344">
                    {hasCheck && <span className="text-[#C87941] mr-3 font-bold flex-shrink-0" data-id="element-345">
                        {checkmark}
                      </span>}
                    <span data-id="element-346">{rest}</span>
                  </li>;
            })}
            </ul>
          </Section>

          <div ref={purchaseRef} id="oferta" data-id="element-347">
            <PurchaseSection orderBumpChecked={orderBumpChecked} data-id="element-348" />
          </div>

          {/* Order Bump */}
          <OrderBump checked={orderBumpChecked} onCheckedChange={setOrderBumpChecked} data-id="element-349" />

          <Section variant="gold" data-id="element-350">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-351">
              {t.invitation.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-352">
              {t.invitation.p1}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-353">
              {t.invitation.p2}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6 font-semibold" data-id="element-354">
              {t.invitation.p3}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-355">
              {t.invitation.p4}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] italic" data-id="element-356">
              {t.invitation.p5}
            </p>
          </Section>

          <div data-id="element-357">
            <img src="https://cdn.magicpatterns.com/uploads/oewaQdGjYsNLcXW4XTGkr5/ChatGPT_Image_22_feb_2026,_09_58_18.webp" alt="Mujer mirando el horizonte al atardecer" className="w-full rounded-2xl shadow-2xl" loading="lazy" decoding="async" data-id="element-358" />
          </div>

          <Section variant="green" data-id="element-359">
            <h2 className="font-serif text-2xl md:text-4xl text-[#2C4A3E] mb-5 md:mb-8" data-id="element-360">
              {t.story.title}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-361">
              {t.story.p1}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-362">
              {t.story.p2}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-363">
              {t.story.p3}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-364">
              {t.story.p4}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-4 md:mb-6" data-id="element-365">
              {t.story.p5}
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#4A4A4A] mb-6 md:mb-8 italic" data-id="element-366">
              {t.story.p6}
            </p>
            <div className="mt-6 md:mt-8" data-id="element-367">
              <img src="https://cdn.magicpatterns.com/uploads/r7MQKFSfkExLDzcoM7ckGb/ChatGPT_Image_28_nov_2025,_11_11_27B.webp" alt="Mujer sonriendo mientras lee el libro" className="w-full rounded-2xl shadow-2xl" loading="lazy" decoding="async" data-id="element-368" />
            </div>
          </Section>

          <CTA orderBumpChecked={orderBumpChecked} data-id="element-369" />
        </main>
      </div>

      {/* Footer */}
      <footer className="w-full bg-[#111D11] border-t border-[#D4A574]/20 mt-0" data-id="element-370">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14" data-id="element-371">
          <div className="mb-8 md:mb-10" data-id="element-372">
            <h3 className="font-serif text-base md:text-lg text-[#E8D5B5] font-bold text-center mb-5 md:mb-6" data-id="element-373">
              Derechos de Autor y Descargo de Responsabilidad
            </h3>
            <p className="text-xs md:text-sm text-[#D4C4A8] mb-4 leading-relaxed" data-id="element-374">
              <span className="inline-flex items-center gap-1" data-id="element-375">
                <span className="text-[#D4A574]" data-id="element-376">©</span>
                <span data-id="element-377">
                  2026 – Darren Porras – Todos los derechos reservados.
                </span>
              </span>
            </p>
            <p className="text-xs md:text-sm text-[#A89880] leading-relaxed mb-4" data-id="element-378">
              Ninguna parte de este libro puede ser reproducida, distribuida o
              transmitida en cualquier forma o por cualquier medio —
              electrónico, mecánico, fotocopia, grabación u otro — sin el
              permiso previo por escrito del autor, excepto en el caso de citas
              breves utilizadas en reseñas, artículos o trabajos académicos.
            </p>
            <p className="text-xs md:text-sm text-[#A89880] leading-relaxed" data-id="element-379">
              Este libro es una obra de autoría original. El autor retiene todos
              los derechos sobre los conceptos, el texto y la propiedad
              intelectual contenidos en el mismo. Cualquier uso no autorizado,
              duplicación o adaptación de este material sin consentimiento
              explícito por escrito está estrictamente prohibido y sujeto a las
              leyes de derechos de autor aplicables.
            </p>
          </div>
          <div className="w-full h-px bg-[#D4A574]/20 mb-6 md:mb-8" data-id="element-380" />
          <div className="flex justify-center gap-6 md:gap-10 mb-6 md:mb-8" data-id="element-381">
            <a href="#" className="text-xs md:text-sm text-[#D4C4A8] hover:text-[#E8B86D] transition-colors duration-200 underline underline-offset-2" data-id="element-382">
              Términos y Políticas
            </a>
            <a href="#" className="text-xs md:text-sm text-[#D4C4A8] hover:text-[#E8B86D] transition-colors duration-200 underline underline-offset-2" data-id="element-383">
              Devoluciones
            </a>
          </div>
          <p className="text-center text-xs md:text-sm text-[#A89880]" data-id="element-384">
            © 2026 Darren Porras. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </>;
}